import { useState, useEffect, useRef } from 'react';
import { getBookings, getBookingsCount, Booking } from '../lib/bookings';
import { useDataCache } from '../contexts/DataCacheContext';
// import { useTabStability } from './useTabStability'; // TEMPORARILY DISABLED

interface BookingsCache {
  bookings: Booking[];
  totalCount: number;
}

export function useBookings(businessId?: string, options?: {
  startDate?: Date;
  endDate?: Date;
  customerId?: string;
}) {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const isMountedRef = useRef(true);
  const { getCache, setCache } = useDataCache();
  // const { shouldSkipOperation } = useTabStability(); // TEMPORARILY DISABLED

  const loadBookings = async (forceRefresh = false) => {
    // TEMPORARILY DISABLED: Skip loading during tab switches unless explicitly forced
    // if (!forceRefresh && shouldSkipOperation('booking data fetch')) {
    //   return;
    // }

    setError(null);

    if (!businessId) {
      setLoading(false);
      setBookings([]);
      setHasMore(false);
      setTotalCount(0);
      return;
    }

    // Check cache first - include filter options in cache key
    const cacheKey = `bookings-${businessId}-${options?.startDate?.getTime() || 'none'}-${options?.endDate?.getTime() || 'none'}-${options?.customerId || 'none'}`;
    
    if (!forceRefresh) {
      const cached = getCache<BookingsCache>(cacheKey);
      if (cached) {
        console.log('Using cached bookings data');
        setBookings(cached.bookings);
        setTotalCount(cached.totalCount);
        setHasMore(false);
        setLoading(false);
        return;
      }
    }

    setLoading(true);

    try {

      // Load all bookings without pagination but with filtering
      const [data, count] = await Promise.all([
        getBookings(businessId, options).catch(err => {
          console.error('Error in getBookings:', err);
          throw new Error('Unable to load bookings. Please try again.');
        }),
        getBookingsCount(businessId)
      ]);

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setBookings(data);
        setTotalCount(count);
        setHasMore(false); // No pagination, so no more items to load
        
        // Update cache
        setCache(cacheKey, {
          bookings: data,
          totalCount: count
        });
      }

    } catch (err) {
      console.error('Error loading bookings:', err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load bookings');
        setBookings([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    
    // Check if we already have cached data for this business and filter options
    const cacheKey = `bookings-${businessId}-${options?.startDate?.getTime() || 'none'}-${options?.endDate?.getTime() || 'none'}-${options?.customerId || 'none'}`;
    const cached = getCache<BookingsCache>(cacheKey);
    
    if (cached) {
      // We have valid cached data, use it immediately without loading state
      console.log('Using cached bookings data on mount');
      setBookings(cached.bookings);
      setTotalCount(cached.totalCount);
      setHasMore(false);
      setLoading(false);
    } else {
      // No cache, load fresh data
      loadBookings();
    }
    
    return () => {
      isMountedRef.current = false;
    };
  }, [businessId, options?.startDate, options?.endDate, options?.customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { 
    bookings, 
    loading, 
    loadingMore,
    error, 
    hasMore,
    totalCount,
    refetch: () => loadBookings(true) // Force refresh when explicitly called
  };
}